import _isBoolean from 'lodash/isBoolean';
import _toFinite from 'lodash/toFinite';

// Persistance version
export const PERSIST_STORE_VERSION = 7;

// Async operations
export const ASYNC_OP_STATUS_PENDING = 'pending';
export const ASYNC_OP_STATUS_SUCCESS = 'success';
export const ASYNC_OP_STATUS_ERROR = 'error';
export const ASYNC_OP_STATUS_OK = 'ok';
export const ASYNC_OP_STATUS_ABORTED = 'aborted';
export const ASYNC_OP_STATUS_IGNORED = 'ignored';

export const ASYNC_OP_TIMEOUT_MS = 30000;

// WS REPLY STATUS
export const WS_REPLY_STATUS_OK = 'ok';
export const WS_REPLY_STATUS_ERROR = 'error';

// WS TOPICS
// Phoenix
export const WS_TOPIC_PHOENIX = 'phoenix';
// Async operations
export const WS_TOPIC_ASYNC = 'async_operations';
// Installations
export const WS_TOPIC_INSTALLATIONS = 'installations';
export const WS_TOPIC_ENTITIES = 'entities';
export const WS_TOPIC_CUSTOMERS = 'customers';
export const WS_TOPIC_INSTALLATION_LOGS = 'installation_logs';
export const WS_TOPIC_PLANNINGS = 'plannings';
export const WS_TOPIC_SCENARIOS = 'scenarios';
// Devices
export const WS_TOPIC_DEVICES = 'devices';
export const WS_TOPIC_CONFIG = 'config';
export const WS_TOPIC_SLAVES = 'slaves';
export const WS_TOPIC_DEVICE_LOGS = 'device_logs';
export const WS_TOPIC_DEVICE_SMS = 'device_sms';
// GraphQL (topic name is unique)
export const WS_TOPIC_GRAPHQL = '__absinthe__:control';

// WS subscriptions ref (only for GraphQL)
export const WS_GRAPHQL_SUBSCRIPTIONS_REF = 'gql_subs';

// WS close codes
export const WS_CODE_CLOSE_NO_STATUS = 1005;
export const WS_CODE_CLOSE_ABNORMAL = 1006;

// UI
export const TABLE_ROW_HEIGHT = 52;

export const HEADER_HEIGHT = 60;
export const FOOTER_HEIGHT = 40;

export const SHOW_VIEW_HEADER_HEIGHT = 145; // Includes title zone + tabs

export const CARD_HEADER_HEIGHT = 64;

// OP
export const ONEPARK_COLOR = 'rgb(12, 179, 228)';
export const FLOW_COLOR = 'rgb(4, 36, 156)';
export const FLOW_COLOR_SECONDARY = 'rgba(4, 36, 156, .7)';
export const ONEPARK_LINK_ADMIN_BOOKING = 'https://admin.onepark.fr/admin/bookings/';
export const ONEPARK_LINK_BACKOFFICE = 'https://backoffice.onepark.fr/#/app/business-subscriptions/';

// Link page
export const ACCOUNT_LINK_RESULT_INVALID = 'invalid';
export const ACCOUNT_LINK_RESULT_OK = 'ok';
export const ACCOUNT_LINK_RESULT_OK_NO_VALIDATION = 'ok_no_validation';

// Forms
export const KEYPAD_CODE_MAX_LENGTH = 6;

// Price Grid Calculation Modes
export const PRICE_GRID_CALCULATION_MODE_PRORATED = 'PRORATED';
export const PRICE_GRID_CALCULATION_MODE_THRESHOLD = 'THRESHOLD';
export const PRICE_GRID_CALCULATION_MODE_HYBRID = 'HYBRID';

export const PRICE_GRID_CALCULATION_MODES = [
  {
    key: 'PRORATED',
    value: PRICE_GRID_CALCULATION_MODE_PRORATED,
    labelId: 'priceGridCalculationMode.prorated',
  },
  {
    key: 'THRESHOLD',
    value: PRICE_GRID_CALCULATION_MODE_THRESHOLD,
    labelId: 'priceGridCalculationMode.threshold',
  },
  {
    key: 'HYBRID',
    value: PRICE_GRID_CALCULATION_MODE_HYBRID,
    labelId: 'priceGridCalculationMode.hybrid',
  },
];

// Lists
// Filters
const OPERATOR_EQ = 'eq';
const OPERATOR_GT = 'gt';
const OPERATOR_GTEQ = 'gteq';
const OPERATOR_ILIKE = 'ilike';
const OPERATOR_ISNULL = 'isNull';
const OPERATOR_LIKE = 'like';
const OPERATOR_LT = 'lt';
const OPERATOR_LTEQ = 'lteq';
const OPERATOR_NEQ = 'neq';

export const FILTERS_OPERATORS = [
  OPERATOR_EQ,
  OPERATOR_GT,
  OPERATOR_GTEQ,
  OPERATOR_ILIKE,
  OPERATOR_ISNULL,
  OPERATOR_LIKE,
  OPERATOR_LT,
  OPERATOR_LTEQ,
  OPERATOR_NEQ,
];

export const SEARCHFIELD_TYPES = {
  string: {
    operators: [OPERATOR_ILIKE, OPERATOR_LIKE, OPERATOR_EQ, OPERATOR_NEQ],
    defaultOperator: (scope) => {
      switch (scope) {
        case 'like':
          return OPERATOR_LIKE;
        case 'eq':
          return OPERATOR_EQ;
        default:
          return OPERATOR_ILIKE;
      }
    },
  },
  numerical: {
    operators: [OPERATOR_EQ, OPERATOR_NEQ, OPERATOR_GT, OPERATOR_GTEQ, OPERATOR_LT, OPERATOR_LTEQ],
    defaultOperator: () => OPERATOR_EQ,
    setValueTransform: (value) => (_toFinite(value)),
  },
  boolean: {
    operators: [OPERATOR_EQ],
    defaultOperator: () => OPERATOR_EQ,
    setValueTransform: (value) => (_isBoolean(value) ? value : (value === 'true')),
  },
  date: {
    operators: [OPERATOR_EQ, OPERATOR_GT, OPERATOR_GTEQ, OPERATOR_ISNULL, OPERATOR_LT, OPERATOR_LTEQ, OPERATOR_NEQ],
    defaultOperator: (scope) => {
      switch (scope) {
        case 'after':
          return OPERATOR_GTEQ;
        case 'before':
          return OPERATOR_LTEQ;
        case 'isNull':
          return OPERATOR_ISNULL;
        default:
          return OPERATOR_EQ;
      }
    },
  },
  enum: {
    operators: [OPERATOR_EQ, OPERATOR_NEQ, OPERATOR_ISNULL],
    setValueTransform: (value) => (value === 'true' ? true : value),
    defaultOperator: () => OPERATOR_EQ,
  },
  resource: {
    operators: [OPERATOR_EQ],
    defaultOperator: () => OPERATOR_EQ,
    setValueTransform: (value) => (_toFinite(value)),
  },
};

export const ONLY_ACTIVE_FILTER = {
  field: 'active',
  operator: OPERATOR_EQ,
  value: true,
};

export const ONLY_NON_ARCHIVED_FILTER = {
  field: 'archived',
  operator: OPERATOR_EQ,
  value: false,
};

// Sort
const DIRECTION_ASC = 'asc';
const DIRECTION_DESC = 'desc';

export const SORT_DIRECTIONS = [DIRECTION_ASC, DIRECTION_DESC];

// Default paginated list result
export const defaultPaginatedListResult = {
  data: null,
  pageNumber: 1,
  pageSize: 20,
  totalEntries: 0,
  totalPages: 1,
};



// WEBPACK FOOTER //
// ./constants/App.js